/**
 * "Link to incident" control (R2 / #478 incident-as-aggregator).
 *
 * DOCTRINE: an evacuation is a PROTECTIVE ACTION / measure — this control makes an
 * evac order / life-safety CAP a protective-action ATTRIBUTE of an incident instead of
 * a competing peer alert. It shows the current link (AUTO-linked by proximity, or
 * operator-linked), lets the operator LINK it to an open (or recently-deactivated)
 * incident via a dropdown, and always allows an UNLINK (reversible; suppresses re-auto-
 * link). Linking to a deactivated incident REACTIVATES it (deactivated → confirmed; the
 * server `reignited` field is the backend token for the same act, §4.2).
 *
 * Mounted on the CAP, GeoBC-evac, and operator-evac detail panels. The caller decides
 * whether the alert is in scope (evac / life-safety) before rendering this.
 */
import { useEffect, useState } from 'react';
import type { LinkableIncident, ProtectiveActionKind } from '@tsr/shared-types';
import {
  fetchLinkableIncidents,
  linkAlertToIncident,
  unlinkAlertFromIncident,
} from './api';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { WILL_REACTIVATE_ON_LINK_SUFFIX, LINKED_REACTIVATED_TOAST } from './incidentVocabulary';

export function LinkToIncidentControl({
  kind,
  alertId,
  incidentId,
  linkMode,
  onChanged,
}: {
  kind: ProtectiveActionKind;
  alertId: string;
  /** The current EFFECTIVE incident link (persisted manual OR auto-projection), or null. */
  incidentId: string | null;
  /** How the link was established: 'manual' | 'auto' | null (unlinked). */
  linkMode: 'manual' | 'auto' | null;
  /** Fired after a successful link/unlink so the panel + queue can refresh. */
  onChanged?: () => void;
}): JSX.Element {
  const [candidates, setCandidates] = useState<LinkableIncident[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  // Local (optimistic) link state so the control reflects a link/unlink immediately,
  // without waiting for the parent panel/layer to re-fetch. Seeded from props.
  const [localIncidentId, setLocalIncidentId] = useState<string | null>(incidentId);
  const [localMode, setLocalMode] = useState<'manual' | 'auto' | null>(linkMode);
  useEffect(() => {
    setLocalIncidentId(incidentId);
    setLocalMode(linkMode);
  }, [incidentId, linkMode]);

  const linked = localIncidentId != null;

  // Load the candidate incidents (open + recently-deactivated, nearest-first) so we can
  // both resolve the linked incident's label and populate the dropdown.
  useEffect(() => {
    const ctl = new AbortController();
    void fetchLinkableIncidents(kind, alertId, ctl.signal)
      .then((list) => {
        setCandidates(list);
        // Default the picker to the nearest auto-radius candidate.
        const preferred = list.find((i) => i.within_auto_radius) ?? list[0];
        setSelected((cur) => cur || preferred?.id || '');
      })
      .catch(() => setCandidates([]));
    return () => ctl.abort();
  }, [kind, alertId]);

  const linkedIncident = candidates.find((i) => i.id === localIncidentId);
  const linkedLabel = linkedIncident
    ? `${linkedIncident.incident_number} — ${linkedIncident.name}`
    : 'a tracked incident';

  const optionLabel = (i: LinkableIncident): string => {
    const dist = i.distance_km > 0 ? ` · ${i.distance_km.toFixed(0)} km` : '';
    const react = i.is_deactivated ? WILL_REACTIVATE_ON_LINK_SUFFIX : '';
    return `${i.incident_number} — ${i.name}${dist}${react}`;
  };

  const doLink = (incId: string): void => {
    if (busy || !incId) return;
    setBusy(true);
    void linkAlertToIncident(kind, alertId, incId, getSessionId())
      .then((r) => {
        showToast(
          r.reignited ? LINKED_REACTIVATED_TOAST : 'Linked to incident as a protective action.',
          { variant: 'success' },
        );
        setLocalIncidentId(incId);
        setLocalMode('manual');
        setPicking(false);
        onChanged?.();
      })
      .catch((err) => showToast((err as Error).message || 'Could not link.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const doUnlink = (): void => {
    if (busy) return;
    setBusy(true);
    void unlinkAlertFromIncident(kind, alertId, getSessionId())
      .then(() => {
        showToast('Unlinked — this evacuation stands alone again.', { variant: 'success' });
        setLocalIncidentId(null);
        setLocalMode(null);
        onChanged?.();
      })
      .catch((err) => showToast((err as Error).message || 'Could not unlink.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  return (
    <section
      className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 space-y-2"
      data-testid="link-to-incident"
    >
      <p className="text-[10px] uppercase tracking-wider text-slate-500">Protective action</p>

      {linked ? (
        <div className="space-y-2" data-testid="link-linked-state">
          <p className="text-sm">
            Linked to <span className="font-semibold">{linkedLabel}</span>
            <span
              className="ml-2 inline-flex items-center rounded bg-slate-700/70 px-1.5 py-0.5 text-[10px] font-medium text-slate-300"
              data-testid="link-mode-chip"
            >
              {localMode === 'auto' ? 'auto-linked' : 'linked by operator'}
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="link-unlink"
              disabled={busy}
              onClick={doUnlink}
              className="text-xs py-1.5 px-3 rounded bg-slate-700/90 hover:bg-slate-700 border border-slate-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
            >
              {busy ? 'Working…' : 'Unlink'}
            </button>
            <button
              type="button"
              data-testid="link-change"
              disabled={busy}
              onClick={() => setPicking((v) => !v)}
              className="text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 font-medium"
            >
              Change…
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400" data-testid="link-standalone-state">
          Standalone — not part of a tracked incident.
        </p>
      )}

      {(!linked || picking) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="link-picker">
          <select
            data-testid="link-incident-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={busy || candidates.length === 0}
            className="text-xs py-1.5 px-2 rounded bg-slate-800 border border-slate-600 text-slate-100 max-w-[16rem]"
          >
            {candidates.length === 0 && <option value="">No open incidents nearby</option>}
            {candidates.map((i) => (
              <option key={i.id} value={i.id}>
                {optionLabel(i)}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="link-confirm"
            disabled={busy || !selected}
            onClick={() => doLink(selected)}
            className="text-xs py-1.5 px-3 rounded bg-violet-700/90 hover:bg-violet-700 border border-violet-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? 'Working…' : 'Link to incident'}
          </button>
        </div>
      )}
    </section>
  );
}
